import Header from '@/components/common/MainContentHeader';
import useI18n from '@/hooks/useI18n';
import { UserContext } from '@/context/UserContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/common/Button';
import { toast } from 'react-toastify';
import Loading from '@/components/common/Loading';
import { formatCampaignDate, formatTimeFromISO } from '@/utils/dateUtils';
import { getUsedPlatforms } from '@/utils/socialplatformUtils';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import LinkShareCard from '@/components/LinkShareCard/LinkShare';
import Model from '@/components/common/models/Model';
import IconButton from '@/components/common/IconButton';
import { BrandMemberRole } from '@/types/enums';
import ToolTip from '@/components/common/ToolTip';
import { generateSmartLink } from '@/utils/smartLinkUtils';
import { toCampaignShareLink } from '@/utils/campaignShareLinkUtils';
import {
  useGetBrandCampaignsInfiniteQuery,
  useReRunCampaignMutation,
} from '@/hooks/queries/useCampaignQueries';
import { formatPercent, formatMoney } from '@/utils/numberUtils';
import { useQueryClient } from '@tanstack/react-query';
import RefreshButton from '@/components/common/RefreshButton';
import { useForceRefresh } from '@/hooks/useForceRefresh';

const CompletedCampaign = () => {
  const { t } = useI18n();
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The cards' engagement rate is cached server-side; refetch under
  // force-refresh (?refresh=true) so it is recomputed, not re-served.
  const { refresh, isRefreshing, secondsRemaining } = useForceRefresh(
    () =>
      queryClient.refetchQueries({ queryKey: ['campaigns'], type: 'active' }),
    'campaign-list-completed',
  );
  const [reRunLoadingId, setReRunLoadingId] = useState<string | null>(null);
  const [copyLink, setCopyLink] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleShareModalClose = () => setIsShareModalOpen(false);

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;

  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const isRegularBrandUser = useMemo(
    () => user?.brandMemberRole === BrandMemberRole.User,
    [user?.brandMemberRole],
  );

  const {
    data: campaignsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useGetBrandCampaignsInfiniteQuery('completed');

  const completedCampaigns =
    campaignsData?.pages.flatMap((page: any) => page?.campaigns ?? []) ?? [];

  const queryIsLoading = isLoading || isFetchingNextPage;

  const reRunMutation = useReRunCampaignMutation();

  const handleShareModalOpen = (id: string) => {
    const campaign = completedCampaigns.find((campaign) => campaign._id === id);
    if (campaign && campaign.smartLinkId) {
      const smartLink = generateSmartLink(campaign.smartLinkId);
      setCopyLink(toCampaignShareLink(smartLink));
    } else {
      setCopyLink('');
    }
    setIsShareModalOpen(true);
  };

  const reRunCompletedCampaign = async (campaignId: string) => {
    setReRunLoadingId(campaignId);
    try {
      const response = await reRunMutation.mutateAsync(campaignId);
      if (response) {
        navigate(`/edit-campaign/${response._id}`, {
          state: { from: 'completed' },
        });
      }
    } catch (error) {
      toast.error(t('wallet.errors.spending'));
    }
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const getTitle = () => {
    if (user && user.brand) {
      return `${user.brand.name}'s`;
    }
    return '';
  };

  return (
    <>
      <div className="bg-white h-[calc(100%-152px)] overflow-visible">
        <div className="p-[24px] lg:bg-[#F0F5FF] bg-white flex items-start justify-between gap-4">
          <Header
            title={getTitle()}
            subtitle={String(t('common.completedCampaign'))}
          />
          <RefreshButton
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            secondsRemaining={secondsRemaining}
          />
        </div>
        {completedCampaigns.map((campaign) => {
          const startDate = formatCampaignDate(campaign.startDate, false);
          const endDate = formatCampaignDate(campaign.endDate ?? '', false);
          const endTime = formatTimeFromISO(campaign?.endDate ?? '');
          const data = [
            {
              label: t('common.budget'),
              value: formatMoney(campaign.campaignBudget),
            },
            {
              label: t('common.participant'),
              value: isDummyUser ? '2k' : campaign.totalParticipants,
            },
            {
              label: t('common.engagement'),
              value: isDummyUser
                ? '20%'
                : formatPercent(campaign.engagementRate),
            },
            // {
            //   label: t('common.earnedMediaValue'),
            //   value: '€400,000 (10x)',
            // },
            {
              label: t('common.totalSales'),
              value: isDummyUser
                ? formatMoney(80200)
                : formatMoney(campaign.totalSales ?? 0),
            },
          ];

          const platform = getUsedPlatforms(campaign.containType);
          return (
            <div
              className="bg-white px-[12px] pt-[12px] sm:px-[24px] sm:pt-[24px] f"
              key={campaign._id}
            >
              <div className="border border-[#E5E9EB] rounded-3xl p-[24px] flex flex-col gap-[24px]">
                <div>
                  <div className="flex justify-between">
                    <span className="text-[#121111] text-lg font-normal capitalize">
                      {campaign.brandName}
                    </span>
                    <div className="flex gap-4 items-center">
                      <ToolTip text={String(t('links.share'))} position="top">
                        <button
                          onClick={() => handleShareModalOpen(campaign?._id)}
                          className="hover:bg-gray-100 rounded-full p-1"
                        >
                          <img
                            src="assets/svgs/fi_share.svg"
                            className="h-[16px] w-[16px]"
                            alt={String(t('campaign.navigation.shareIcon'))}
                          />
                        </button>
                      </ToolTip>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex gap-[8px] items-center">
                      <img
                        src="/assets/fi_calendar.svg"
                        className="h-[16px] w-[16px]"
                        alt={String(t('campaign.navigation.calendarIcon'))}
                      />
                      <span>
                        {startDate} -{' '}
                        {campaign.runCampaignTillMoneyEnd
                          ? t('common.untilBudgetEnds')
                          : endDate}
                      </span>
                    </div>
                    {campaign.endDate || !campaign.runCampaignTillMoneyEnd ? (
                      <div className="flex gap-[8px] items-center">
                        <img
                          src="/assets/fi_clock.svg"
                          className="h-[16px] w-[16px]"
                          alt={String(t('campaign.navigation.clockIcon'))}
                        />
                        <span>{endTime}</span>
                      </div>
                    ) : null}
                    <div className="border bg-[#00AE3E1A] rounded-full px-[10px]">
                      <span className="text-sm font-medium text-[#00AE3E]">
                        {t('common.completed')}
                      </span>
                    </div>
                  </div>{' '}
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-[32px]">
                    {data.map((item, index) => (
                      <div key={index}>
                        {item.label === t('common.earnedMediaValue') ? (
                          <div className="text-[#9B919D] font-normal text-sm mb-2">
                            {t('common.earnedMediaValue')}{' '}
                            <ToolTip
                              text={String(t('common.EMVTooltipDesc'))}
                              position="top"
                            >
                              <span className="inline-block">ⓘ</span>
                            </ToolTip>
                          </div>
                        ) : (
                          <div className="text-[#9B919D] font-normal text-sm mb-2">
                            {item.label}
                          </div>
                        )}
                        <div className={`text-lg font-normal  text-[#292828]`}>
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!isRegularBrandUser && (
                    <Button
                      // disabled={isReRunLoading}
                      className={`${reRunLoadingId === campaign._id ? 'opacity-75' : ''}`}
                      onClick={() => {
                        if (campaign._id) {
                          reRunCompletedCampaign(campaign._id);
                        }
                      }}
                      type="button"
                    >
                      {t('common.reRun')}
                    </Button>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-[#9B919D] font-normal text-sm mb-2">
                      {t('common.platform')}
                    </div>
                    <div className="flex gap-4">
                      <div className="flex flex-row gap-3">
                        {platform === 'both' || platform === 'tiktok' ? (
                          <img
                            src="assets/PlateformTikTok.svg"
                            className="w-[24px] h-[24px]"
                            alt={String(t('campaign.platforms.tiktokIcon'))}
                          />
                        ) : null}

                        {platform === 'both' || platform === 'instagram' ? (
                          <img
                            src="assets/PlateformInsta.svg"
                            className="w-[24px] h-[24px]"
                            alt={String(t('campaign.platforms.instagramIcon'))}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <ToolTip text={String(t('links.details'))} position="top">
                    <IconButton
                      rel="noopener noreferrer"
                      onClick={() =>
                        navigate(`/campaign/${campaign._id}`, {
                          state: { from: 'completed' },
                        })
                      }
                      src="assets/fi_full_right_arrow.svg"
                      className="hover:bg-gray-100 rounded-full p-2"
                    />
                  </ToolTip>
                </div>
              </div>
            </div>
          );
        })}
        {queryIsLoading && (
          <div className="flex justify-center items-center py-4">
            <Loading
              centeredToParent={true}
              style={{
                top: 'calc(50% + 60px)',
              }}
            />
          </div>
        )}

        {hasNextPage && !queryIsLoading && (
          <div ref={sentinelRef} className="h-1" />
        )}

        {isShareModalOpen && (
          <Model isOpen={isShareModalOpen} onClose={handleShareModalClose}>
            <LinkShareCard
              handleShareModalClose={handleShareModalClose}
              link={copyLink}
            />
          </Model>
        )}
      </div>
    </>
  );
};

export default CompletedCampaign;
